import {useQuery, gql} from '@apollo/client';
import {useMemo} from 'react';
import {
  GetReviewInvitationDetails,
  GetReviewInvitationDetails_reviewDetails,
  GetReviewInvitationDetailsVariables,
} from '../../../schema-types/GetReviewInvitationDetails';
import {
  REVIEW_INVITATION_DETAILS_FRAGMENT,
  REVIEW_INVITATION_DETAILS_FRAGMENT_SLOW,
} from '../../../graphql/fragments';
import {
  GetReviewInvitationDetailsSlow,
  GetReviewInvitationDetailsSlowVariables,
  GetReviewInvitationDetailsSlow_reviewDetails_contacts,
} from '../../../schema-types/GetReviewInvitationDetailsSlow';

const GET_REVIEW_DETAILS = gql`
  query GetReviewInvitationDetails($valueId: UUID!, $activeOnly: Boolean) {
    reviewDetails(valueId: $valueId, keyId: "review_id", activeOnly: $activeOnly) {
      ...ReviewInvitationDetailsFragment
    }
  }
  ${REVIEW_INVITATION_DETAILS_FRAGMENT}
`;

const GET_REVIEW_DETAILS_SLOW = gql`
  query GetReviewInvitationDetailsSlow($valueId: UUID!, $activeOnly: Boolean) {
    reviewDetails(valueId: $valueId, keyId: "review_id", activeOnly: $activeOnly) {
      ...ReviewInvitationDetailsSlowFragment
    }
  }
  ${REVIEW_INVITATION_DETAILS_FRAGMENT_SLOW}
`;

export const useReviewInvitation = (reviewId: string) => {
  const {data, error, loading} = useQuery<
    GetReviewInvitationDetails,
    GetReviewInvitationDetailsVariables
  >(GET_REVIEW_DETAILS, {
    variables: {
      valueId: reviewId,
      activeOnly: false,
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: slowData,
    error: slowError,
    loading: slowLoading,
  } = useQuery<GetReviewInvitationDetailsSlow, GetReviewInvitationDetailsSlowVariables>(
    GET_REVIEW_DETAILS_SLOW,
    {
      variables: {
        valueId: reviewId,
        activeOnly: false,
      },
      fetchPolicy: 'network-only',
    }
  );

  const reviewInvitation = useMemo(() => {
    if (!data?.reviewDetails) {
      return null;
    }

    return {
      ...data.reviewDetails,
      contacts: slowData?.reviewDetails?.contacts,
    } as GetReviewInvitationDetails_reviewDetails & {
      contacts: GetReviewInvitationDetailsSlow_reviewDetails_contacts[];
    };
  }, [data, slowData]);

  return {
    reviewInvitation,
    reviewInvitationError: error || slowError,
    reviewInvitationLoading: loading,
    reviewInvitationLoadingSlow: slowLoading,
  };
};
